import React, { useRef, useState } from 'react';
import { Badge, type GalleryImage as DSGalleryImage } from '../../design-system/components';
import type { PropertyBadge } from '../../domain/property/badges';
import { CameraIcon } from './icons';

// ─────────────────────────────────────────────
// Hero — v2(2026-09-21): 좌상단 배지(buildBadges, 최대 3) · 우상단 1 / N · 우하단 HUG 분양보증 · 하단 그라디언트
// v2h(2026-09-22): 목업 04 — 단지명(h1)·주소·시공사 줄을 이미지 하단에 오버레이, 높이 250(모바일)/340(PC),
//   하단 썸네일 줄 제거(사진은 스와이프·화살표 + '홍보자료' 섹션에서 전부 볼 수 있음).
// ─────────────────────────────────────────────
export const Hero: React.FC<{
  images: DSGalleryImage[];
  searchVolume?: number | null;
  totalCount: number;
  /** buildBadges(property) — 대표혜택 → 할인율 → '분양예정' (상태 단어 배지 없음) */
  badges?: PropertyBadge[];
  /** Property.hug_guarantee */
  hugGuarantee?: boolean;
  /** 단지명 — 이미지 하단 오버레이 h1 (v2h) */
  title?: string;
  /** 주소 — 단지명 아래 */
  address?: string | null;
  /** 단지명 위 한 줄 (예: '대우건설 시공') */
  eyebrow?: string | null;
}> = ({ images, searchVolume, totalCount, badges = [], hugGuarantee = false, title, address, eyebrow }) => {
  const [idx, setIdx] = useState(0);
  const startX = useRef<number | null>(null);
  const visible = images.length > 0 ? images : [{ src: '', alt: '' }];
  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx < -50) setIdx((i) => (i + 1) % visible.length);
    else if (dx > 50) setIdx((i) => (i - 1 + visible.length) % visible.length);
    startX.current = null;
  };

  const current = visible[idx];

  return (
    <div>
      {/* 메인 이미지 — 목업 04: 모바일 250px · PC 340px */}
      <div
        className="relative h-[250px] overflow-hidden bg-block md:h-[320px] lg:h-[340px]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {current.src ? (
          <img
            src={current.src}
            alt={current.alt ?? ''}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-mute2 text-[12px] uppercase tracking-widest font-mono">
            no image
          </div>
        )}

        {/* 하단 그라디언트 (목업 히어로 오버레이 — 배지·HUG 가독성) */}
        {(current.src || title) && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: title
                ? 'linear-gradient(to top, rgba(15,23,42,.88), rgba(15,23,42,.1) 55%, transparent)'
                : 'linear-gradient(to top, rgba(15,23,42,.55), rgba(15,23,42,0) 45%)',
            }}
          />
        )}

        {/* 하단 오버레이: 시공사 줄 · 단지명(h1) · 주소 (목업 04) */}
        {title && (
          <div className={`pointer-events-none absolute bottom-3.5 left-4 flex flex-col gap-1 text-white lg:bottom-6 lg:left-6 ${hugGuarantee ? 'right-32' : 'right-4'}`}>
            {eyebrow && <span className="text-[11.5px] font-semibold text-[#FDBA74]">{eyebrow}</span>}
            <h1 className="text-[22px] font-extrabold leading-[1.25] tracking-tight text-white [text-shadow:0_1px_8px_rgba(15,23,42,.35)] lg:text-[32px] lg:leading-[1.3]">{title}</h1>
            {address && <span className="text-[12.5px] text-[#E2E8F0] lg:text-[14px] lg:text-[#CBD5E1]">{address}</span>}
          </div>
        )}

        {/* 좌상단: v2 배지(최대 3) — 없으면 검색량 */}
        {badges.length > 0 ? (
          <div className="absolute left-3 top-3 right-20 flex flex-wrap gap-1">
            {badges.slice(0, 3).map((b) => (
              <Badge key={b.key} tone={b.tone} size="tag" shape="pill" ariaLabel={b.ariaLabel}>
                {b.key === 'headline' ? `대표혜택 ${b.label}` : b.label}
              </Badge>
            ))}
          </div>
        ) : typeof searchVolume === 'number' && searchVolume > 0 ? (
          <span className="absolute left-3 top-3 rounded-ca-sm bg-frame/92 px-2 py-1 text-[11px] font-bold text-ink backdrop-blur">
            검색량:{searchVolume.toLocaleString()}건
          </span>
        ) : null}

        {/* 우상단: 갤러리 수 (목업 .tagpill 1 / N) */}
        {totalCount > 0 && (
          <span className="num absolute right-3 top-3 flex items-center gap-1 rounded-full bg-ink/70 px-2 py-[3px] text-[10px] font-extrabold text-white backdrop-blur">
            <CameraIcon /> {idx + 1} / {totalCount}
          </span>
        )}

        {/* 우하단: HUG 분양보증 (Property.hug_guarantee) */}
        {hugGuarantee && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-frame/95 px-2.5 py-1 text-[10.5px] font-extrabold text-ink shadow-ca-card">
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="text-ok">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
            HUG 분양보증
          </span>
        )}

        {/* 좌우 화살표 (이미지 2개 이상) */}
        {visible.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx((i) => (i - 1 + visible.length) % visible.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 hidden md:flex h-9 w-9 items-center justify-center rounded-full bg-frame/80 text-ink shadow-ca-1 backdrop-blur hover:bg-frame"
              aria-label="이전 이미지"
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => (i + 1) % visible.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:flex h-9 w-9 items-center justify-center rounded-full bg-frame/80 text-ink shadow-ca-1 backdrop-blur hover:bg-frame"
              aria-label="다음 이미지"
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </>
        )}
      </div>

    </div>
  );
};
